import { useEffect, useRef, useState } from "react";
import { Notification } from "@rio-cloud/rio-uikit";
import { useT } from "@hooks";
import type { TableDataProps } from "@components/Table/Settings";

export const sortSettingsOptions = <TOption extends TableDataProps>(options?: TOption[]): TOption[] =>
  [...(options ?? [])].sort((a, b) => {
    const aInativo = a.active === false;
    const bInativo = b.active === false;

    if (aInativo !== bInativo) return aInativo ? 1 : -1;

    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
  });

export interface ISettingsCrudSelection {
  id: number;
  name: string;
}

export type BuildEditSelection<TSelected> = (
  id: number,
  name: string,
  type?: string,
  expenseType?: string,
  categoryName?: string,
  categoryId?: number
) => TSelected;

interface IUseSettingsCrudPageParams<
  TResponse,
  TOption extends TableDataProps,
  TSelected extends ISettingsCrudSelection,
> {
  translationPrefix: string;
  reloadKey?: string;
  fetchService: () => Promise<TResponse[]>;
  mountOption: (data: TResponse[]) => TOption[];
  createService: (value: string, selectCategory?: string, selectType?: string) => Promise<unknown>;
  editService: (selected: TSelected, value: string, selectCategory?: string, selectType?: string) => Promise<unknown>;
  inactivateService: (id: number, activate: boolean) => Promise<unknown>;
  buildEditSelection?: BuildEditSelection<TSelected>;
}

const useSettingsCrudPage = <TResponse, TOption extends TableDataProps, TSelected extends ISettingsCrudSelection>({
  translationPrefix,
  reloadKey = "",
  fetchService,
  mountOption,
  createService,
  editService,
  inactivateService,
  buildEditSelection,
}: IUseSettingsCrudPageParams<TResponse, TOption, TSelected>) => {
  const t = useT();
  const [createNewModal, setCreateNewModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [options, setOptions] = useState<TOption[]>([]);
  const [optionSelected, setOptionSelected] = useState<TSelected>();
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [duplicateAlertMessage, setDuplicateAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const message = (key: string) => t(`${translationPrefix}.${key}`);

  const fetchOptions = async () => {
    try {
      const response = await fetchService();

      setOptions(sortSettingsOptions(mountOption(response)));
    } catch (error) {
      console.log("error", error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOptionsRef = useRef(fetchOptions);
  fetchOptionsRef.current = fetchOptions;

  useEffect(() => {
    fetchOptionsRef.current();
  }, [reloadKey]);

  const handleOpenCreate = () => {
    setCreateNewModal(true);
  };

  const handleOpenEdit = (
    id: number,
    name: string,
    type?: string,
    expenseType?: string,
    categoryName?: string,
    categoryId?: number
  ) => {
    setOptionSelected(
      buildEditSelection
        ? buildEditSelection(id, name, type, expenseType, categoryName, categoryId)
        : ({ id, name } as TSelected)
    );
    setEditModal(true);
  };

  const handleOpenDelete = (id: number, name: string) => {
    setOptionSelected({ id, name } as TSelected);
    setDeleteModal(true);
  };

  const handleSaveNewOption = async (value: string, selectCategory?: string, selectType?: string) => {
    try {
      await createService(value, selectCategory, selectType);
      fetchOptions();
      setShowDuplicateAlert(false);
      setCreateNewModal(false);
      Notification.success(message("notification.createSuccess"));
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 409) {
        setDuplicateAlertMessage(message("notification.createDuplicate"));
        setShowDuplicateAlert(true);
      } else {
        Notification.error(message("notification.createError"));
      }
    }
  };

  const handleEditOption = async (value: string, selectCategory?: string, selectType?: string) => {
    if (!optionSelected?.id) return;

    try {
      await editService(optionSelected, value, selectCategory, selectType);
      fetchOptions();
      setEditModal(false);
      Notification.success(message("notification.editSuccess"));
    } catch {
      Notification.error(message("notification.editError"));
    }
  };

  const handleInactivate = async () => {
    if (!optionSelected?.id) return;
    try {
      await inactivateService(optionSelected.id, false);
      fetchOptions();
      setDeleteModal(false);
      Notification.success(message("notification.inactivateSuccess"));
    } catch {
      Notification.error(message("notification.inactivateError"));
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await inactivateService(id, true);
      fetchOptions();
      Notification.success(message("notification.activateSuccess"));
    } catch {
      Notification.error(message("notification.activateError"));
    }
  };

  return {
    options,
    isLoading,
    createNewModal,
    setCreateNewModal,
    editModal,
    setEditModal,
    deleteModal,
    setDeleteModal,
    optionSelected,
    showDuplicateAlert,
    setShowDuplicateAlert,
    duplicateAlertMessage,
    handleOpenCreate,
    handleOpenEdit,
    handleOpenDelete,
    handleSaveNewOption,
    handleEditOption,
    handleInactivate,
    handleActivate,
  };
};

export default useSettingsCrudPage;
